import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { useTranslation, LanguageSwitcher } from '../i18n';
import { Mail, Lock, User, Phone, ArrowRight, ArrowLeft, KeyRound, CreditCard } from 'lucide-react';

export default function Login() {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [smsLoginPhone, setSmsLoginPhone] = useState('');
  const [smsLoginCode, setSmsLoginCode] = useState('');
  const [smsLoginSent, setSmsLoginSent] = useState(false);
  const [smsLoginError, setSmsLoginError] = useState('');
  const [phoneNotRegistered, setPhoneNotRegistered] = useState(false);
  const [smsLoginLoading, setSmsLoginLoading] = useState(false);
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [pwdError, setPwdError] = useState('');
  const { login, register } = useAuth();

  const validatePassword = (pwd) => {
    if (pwd.length < 8) return isGeorgian ? 'მინიმუმ 8 სიმბოლო' : 'At least 8 characters';
    if (!/[A-Z]/.test(pwd)) return isGeorgian ? 'მინიმუმ 1 დიდი ასო (A-Z)' : 'At least 1 uppercase letter';
    if (!/[a-z]/.test(pwd)) return isGeorgian ? 'მინიმუმ 1 პატარა ასო (a-z)' : 'At least 1 lowercase letter';
    if (!/[0-9]/.test(pwd)) return isGeorgian ? 'მინიმუმ 1 ციფრი' : 'At least 1 number';
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(pwd)) return isGeorgian ? 'მინიმუმ 1 სპეციალური სიმბოლო (!@#$...)' : 'At least 1 special character (!@#$...)';
    return '';
  };

  const validateGeoPhone = (p) => {
    const cleaned = p.replace(/\D/g, '').replace(/^995/, '');
    if (cleaned.length !== 9) return isGeorgian ? 'ნომერი უნდა იყოს 9 ციფრი' : 'Number must be 9 digits';
    if (!cleaned.startsWith('5')) return isGeorgian ? 'ნომერი უნდა იწყებოდეს 5-ით' : 'Number must start with 5';
    return null;
  };

  const handleSendPhoneCode = async () => {
    const err = validateGeoPhone(phone);
    if (err) { setPhoneError(err); return; }
    setPhoneSending(true); setPhoneError('');
    try {
      await authAPI.sendPhoneCode(phone);
      setPhoneCodeSent(true);
    } catch (err) {
      setPhoneError(err.response?.data?.detail || 'Error');
    }
    setPhoneSending(false);
  };

  const handleSmsLoginSend = async () => {
    const err = validateGeoPhone(smsLoginPhone);
    if (err) { setSmsLoginError(err); return; }
    setSmsLoginLoading(true); setSmsLoginError(''); setPhoneNotRegistered(false);
    try {
      await authAPI.smsLoginSend(smsLoginPhone);
      setSmsLoginSent(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      // Phone number not registered → offer to register
      if (status === 404 || detail.toLowerCase().includes('not registered') || detail.includes('არ არის რეგისტრირებული')) {
        setPhoneNotRegistered(true);
        setSmsLoginError(isGeorgian 
          ? 'ეს ნომერი არ არის რეგისტრირებული' 
          : 'This number is not registered');
      } else {
        setSmsLoginError(detail || 'Error');
      }
    }
    setSmsLoginLoading(false);
  };

  const handleSmsLoginVerify = async () => {
    if (!smsLoginCode || smsLoginCode.length < 6) return;
    setSmsLoginLoading(true); setSmsLoginError('');
    try {
      const res = await authAPI.smsLoginVerify(smsLoginPhone, smsLoginCode);
      localStorage.setItem('ctoken', res.data.access_token);
      localStorage.setItem('cuser', JSON.stringify(res.data.user));
      window.location.href = redirectTo;
    } catch (err) {
      setSmsLoginError(err.response?.data?.detail || 'Invalid code');
    }
    setSmsLoginLoading(false);
  };

  const handleVerifyPhone = async () => {
    if (!phoneCode || phoneCode.length < 6) return;
    setPhoneError('');
    try {
      await authAPI.verifyPhoneCode(phone, phoneCode);
      setPhoneVerified(true);
    } catch (err) {
      setPhoneError(err.response?.data?.detail || 'Invalid code');
    }
  };
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Only same-site paths ("/pickup?code=…"), never another website.
  const rawRedirect = searchParams.get('redirect') || '';
  const redirectTo = (rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') && !rawRedirect.startsWith('/\\'))
    ? rawRedirect : '/home';
  const { t, isGeorgian } = useTranslation();

  const handleLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    setLoading(true);
    try { 
      setError(''); 
      await login(email, password); 
      navigate(redirectTo); 
    }
    catch (err) { 
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      // Specific error messages
      if (status === 401 && (detail.toLowerCase().includes('email') || detail.includes('ელ-ფოსტა'))) {
        setError(isGeorgian ? 'ეს ელ-ფოსტა არ არის რეგისტრირებული' : 'This email is not registered');
      } else if (status === 401 && (detail.toLowerCase().includes('password') || detail.includes('პაროლი'))) {
        setError(isGeorgian ? 'არასწორი პაროლი' : 'Wrong password');
      } else {
        setError(detail || (isGeorgian ? 'შესვლის შეცდომა' : 'Login error'));
      }
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await register(email, password, firstName, lastName, idNumber, phone); navigate(redirectTo); }
    catch (err) { setError(err.response?.data?.detail || t('errors.serverError')); }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSuccess(t('auth.resetSent'));
      setMode('reset');
    } catch (err) { setError(err.response?.data?.detail || t('errors.serverError')); }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      await authAPI.resetPassword(email, code, newPassword);
      setSuccess(t('auth.passwordChanged'));
      setMode('login');
      setPassword('');
      setCode('');
      setNewPassword('');
    } catch (err) { setError(err.response?.data?.detail || t('errors.serverError')); }
    setLoading(false);
  };

  const switchMode = (m) => { setMode(m); setError(''); setSuccess(''); };

  return (
    <div style={s.page}>
      <div style={s.glow1} /><div style={s.glow2} />
      <div style={s.card}>
        <div style={s.topRow}>
          <div style={s.brand}>
            <img src="/damitove-logo.png" alt="Damitove" style={{height: 42, width: 'auto'}} />
            <span style={s.brandName}>Dami<span style={{ color: '#8BC63E' }}>tove</span></span>
          </div>
          <LanguageSwitcher style={{ background: '#161821', border: '1px solid #252733', color: '#8789a0' }} />
        </div>

        {/* LOGIN */}
        {mode === 'login' && (
          <>
            <h2 style={s.title}>{t('auth.loginTitle')}</h2>
            <p style={s.sub}>{t('auth.login')}</p>
            {error && <div style={s.error}>{error}</div>}
            {success && <div style={s.success}>{success}</div>}
            <div style={{background:'linear-gradient(135deg, rgba(139,198,62,0.08), rgba(0,180,216,0.05))', border:'1px solid rgba(139,198,62,0.3)', borderRadius:14, padding:'16px 14px', marginBottom:20}}>
              <div style={{fontSize:15,fontWeight:700,color:'#8BC63E',marginBottom:4,display:'flex',alignItems:'center',gap:6}}>⚡ {isGeorgian ? 'სწრაფი შესვლა SMS-ით' : 'Quick Login with SMS'}</div>
              <div style={{fontSize:12,color:'#8789a0',marginBottom:14}}>{isGeorgian ? 'შეიყვანეთ ნომერი და მიიღეთ კოდი' : 'Enter your number and get a code'}</div>
            {!smsLoginSent ? (
              <div style={{...s.field, marginBottom: 0}}>
                <Phone size={16} style={s.icon} />
                <input style={{...s.input, paddingRight: 90}} type="tel" inputMode="tel" autoComplete="tel-national" name="phone"
                  placeholder={isGeorgian ? 'მობილურის ნომერი' : 'Mobile Number'}
                  value={smsLoginPhone} onChange={e => setSmsLoginPhone(e.target.value)} />
                {!validateGeoPhone(smsLoginPhone) && (
                  <button type="button" onClick={handleSmsLoginSend} disabled={smsLoginLoading}
                    style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',padding:'8px 14px',background:'#8BC63E',color:'#07080c',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',minWidth:84,whiteSpace:'nowrap'}}>
                    {smsLoginLoading ? (isGeorgian ? 'იგზავნება...' : 'Sending...') : (isGeorgian ? 'მიიღე SMS' : 'Send')}
                  </button>
                )}
              </div>
            ) : (
              <div>
                <div style={{fontSize:12,color:'#8BC63E',textAlign:'center',marginBottom:10}}>{isGeorgian ? 'კოდი გაგზავნილია' : 'Code sent'} {smsLoginPhone}</div>
                <div style={{display:'flex',gap:8}}>
                  <div style={{...s.field, flex:1, marginBottom:0}}>
                    <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" name="sms-code" style={{...s.input, paddingLeft:14, textAlign:'center', fontSize:24, letterSpacing:10, fontWeight:700, fontFamily:"'Space Mono', monospace"}}
                      placeholder="000000" value={smsLoginCode} onChange={e => setSmsLoginCode(e.target.value.replace(/\D/g,'').slice(0,6))} maxLength={6} autoFocus />
                  </div>
                  <button type="button" onClick={handleSmsLoginVerify} disabled={smsLoginLoading || smsLoginCode.length < 6}
                    style={{padding:'0 24px',background: smsLoginCode.length >= 6 ? '#8BC63E' : '#252733',color: smsLoginCode.length >= 6 ? '#07080c' : '#565870',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',transition:'all 0.2s'}}>
                    {smsLoginLoading ? (isGeorgian ? 'მოწმდება...' : 'Verifying...') : (isGeorgian ? 'შესვლა' : 'Login')}
                  </button>
                </div>
                <div style={{textAlign:'center',marginTop:10}}>
                  <span style={{fontSize:12,color: '#8a8da4',cursor:'pointer',textDecoration:'underline'}} onClick={() => {setSmsLoginSent(false);setSmsLoginCode('');}}>
                    {isGeorgian ? 'თავიდან გაგზავნა' : 'Resend code'}
                  </span>
                </div>
              </div>
            )}
            {smsLoginError && (
              <div style={{fontSize:12,color:'#ff4466',textAlign:'center',marginTop:8}}>
                {smsLoginError}
                {phoneNotRegistered && (
                  <div style={{marginTop:12}}>
                    <button type="button" onClick={() => switchMode('register')}
                      style={{padding:'10px 20px',background:'#8BC63E',color:'#07080c',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                      {isGeorgian ? '+ რეგისტრაცია' : '+ Register now'}
                    </button>
                  </div>
                )}
              </div>
            )}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:12,margin:'20px 0'}}>
              <div style={{flex:1,height:1,background:'#252733'}}/>
              <div style={{fontSize:12,color: '#8a8da4'}}>{isGeorgian ? 'ან ელ-ფოსტით' : 'or with email'}</div>
              <div style={{flex:1,height:1,background:'#252733'}}/>
            </div>
            <div>
              <div style={s.field}><Mail size={16} style={s.icon} /><input style={s.input} type="email" autoComplete="email" autoCapitalize="none" name="email" placeholder={t('auth.email')} value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleLogin(e); }} /></div>
              <div style={s.field}><Lock size={16} style={s.icon} /><input style={s.input} type="password" autoComplete="current-password" name="password" placeholder={t('auth.password')} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleLogin(e); }} /></div>
              <div style={s.forgotLink} onClick={() => switchMode('forgot')}>{t('auth.forgotPassword')}</div>
              {error && (
                <div style={{
                  padding: '12px 14px',
                  background: 'rgba(255,68,102,0.15)',
                  border: '1px solid #ff4466',
                  borderRadius: 8,
                  color: '#ff4466',
                  fontSize: 14,
                  fontWeight: 600,
                  textAlign: 'center',
                  marginBottom: 12,
                  display: 'block',
                }}>
                  ⚠️ {error}
                </div>
              )}
              <button style={{...s.btn, background:'transparent', border:'1px solid #252733', color:'#8789a0'}} type="button" onClick={handleLogin} disabled={loading}>{loading ? t('common.loading') : t('auth.login')} <ArrowRight size={16} /></button>
            </div>
            <div style={{height:1,background:'#252733',margin:'20px 0 16px',display:'none'}}/>
            <div style={{textAlign:'center',fontSize:13,color: '#8a8da4',marginBottom:14,display:'none'}}>{isGeorgian ? 'ან სწრაფი შესვლა SMS-ით' : 'or quick login with SMS'}</div>

            <div style={s.switch}>{t('auth.noAccount')} <span style={s.switchLink} onClick={() => switchMode('register')}>{t('nav.register')}</span></div>
            <div style={s.divider}><span>{t('common.language') === 'ენა' ? 'ან' : 'or'}</span></div>
            <button style={s.pickupBtn} onClick={() => navigate('/pickup')}>{t('parcel.enterPickupCode')}</button>
          </>
        )}

        {/* REGISTER */}
        {mode === 'register' && (
          <>
            <h2 style={s.title}>{t('auth.registerTitle')}</h2>
            <p style={s.sub}>{t('nav.register')}</p>
            {error && <div style={s.error}>{error}</div>}
            <form onSubmit={handleRegister}>
              <div style={s.field}><CreditCard size={16} style={s.icon} /><input style={s.input} placeholder={isGeorgian ? 'პირადი ნომერი' : 'ID Number'} value={idNumber} onChange={e => setIdNumber(e.target.value)} required /></div>
              <div style={s.field}><User size={16} style={s.icon} /><input style={s.input} autoComplete="given-name" placeholder={isGeorgian ? 'სახელი' : 'First Name'} value={firstName} onChange={e => setFirstName(e.target.value)} required /></div>
              <div style={s.field}><User size={16} style={s.icon} /><input style={s.input} autoComplete="family-name" placeholder={isGeorgian ? 'გვარი' : 'Last Name'} value={lastName} onChange={e => setLastName(e.target.value)} required /></div>
              <div style={s.field}><Mail size={16} style={s.icon} /><input style={s.input} type="email" autoComplete="email" autoCapitalize="none" name="email" placeholder={t('auth.email')} value={email} onChange={e => setEmail(e.target.value)} required /></div>
              {/* Phone verification */}
              <div style={s.field}>
                <Phone size={16} style={s.icon} />
                <input style={{...s.input, paddingRight: phoneVerified ? 14 : 90}} type="tel" inputMode="tel" autoComplete="tel-national" name="phone"
                  placeholder={isGeorgian ? 'მობილურის ნომერი' : 'Mobile Number'}
                  value={phone} onChange={e => { setPhone(e.target.value); setPhoneVerified(false); setPhoneCodeSent(false); }} required
                  disabled={phoneVerified} />
                {!phoneVerified && !phoneCodeSent && !validateGeoPhone(phone) && (
                  <button type="button" onClick={handleSendPhoneCode} disabled={phoneSending}
                    style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',padding:'6px 12px',background:'#8BC63E',color:'#07080c',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                    {phoneSending ? '...' : (isGeorgian ? 'კოდი' : 'Verify')}
                  </button>
                )}
                {phoneVerified && (
                  <span style={{position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',color:'#8BC63E',fontSize:16}}>✓</span>
                )}
              </div>
              {phoneCodeSent && !phoneVerified && (
                <div style={{...s.field, display:'flex', gap:8}}>
                  <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" name="phone-code" style={{...s.input, paddingLeft:14, textAlign:'center', fontSize:20, letterSpacing:6, fontWeight:700, fontFamily:"'Space Mono', monospace", flex:1}}
                    placeholder="000000" value={phoneCode} onChange={e => setPhoneCode(e.target.value.replace(/\D/g,'').slice(0,6))} maxLength={6} />
                  <button type="button" onClick={handleVerifyPhone}
                    style={{padding:'0 20px',background:'#8BC63E',color:'#07080c',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                    {isGeorgian ? 'დადასტურება' : 'Confirm'}
                  </button>
                </div>
              )}
              {phoneError && <div style={{fontSize:12,color:'#ff4466',marginBottom:8}}>{phoneError}</div>}
              <div style={s.field}><Lock size={16} style={s.icon} /><input style={s.input} type="password" autoComplete="new-password" name="new-password" placeholder={t('auth.password')} value={password} onChange={e => { setPassword(e.target.value); setPwdError(validatePassword(e.target.value)); }} required /></div>
              {password && pwdError && <div style={{fontSize:12,color:'#ff4466',marginBottom:8}}>{pwdError}</div>}
              {password && !pwdError && <div style={{fontSize:12,color:'#8BC63E',marginBottom:8}}>{isGeorgian ? '✓ პაროლი ძლიერია' : '✓ Password is strong'}</div>}
              <button style={{...s.btn, opacity: (phoneVerified && !pwdError && password) ? 1 : 0.5}} type="submit" disabled={loading || !phoneVerified || pwdError || !password}>{loading ? t('common.loading') : t('auth.registerTitle')} <ArrowRight size={16} /></button>
            </form>
            <div style={s.switch}>{t('auth.haveAccount')} <span style={s.switchLink} onClick={() => switchMode('login')}>{t('auth.loginHere')}</span></div>
          </>
        )}

        {/* FORGOT PASSWORD */}
        {mode === 'forgot' && (
          <>
            <div style={s.backBtn} onClick={() => switchMode('login')}><ArrowLeft size={16} /> {t('common.back')}</div>
            <h2 style={s.title}>{t('auth.forgotPassword')}</h2>
            <p style={s.sub}>{t('auth.resetInstructions')}</p>
            {error && <div style={s.error}>{error}</div>}
            {success && <div style={s.success}>{success}</div>}
            <form onSubmit={handleForgot}>
              <div style={s.field}><Mail size={16} style={s.icon} /><input style={s.input} type="email" autoComplete="email" autoCapitalize="none" name="email" placeholder={t('auth.email')} value={email} onChange={e => setEmail(e.target.value)} required /></div>
              <button style={s.btn} type="submit" disabled={loading}>{loading ? t('common.loading') : t('auth.resetPassword')} <Mail size={16} /></button>
            </form>
          </>
        )}

        {/* RESET PASSWORD */}
        {mode === 'reset' && (
          <>
            <div style={s.backBtn} onClick={() => switchMode('forgot')}><ArrowLeft size={16} /> {t('common.back')}</div>
            <h2 style={s.title}>{t('auth.resetPassword')}</h2>
            <p style={s.sub}>{email}</p>
            {error && <div style={s.error}>{error}</div>}
            {success && <div style={s.success}>{success}</div>}
            <form onSubmit={handleReset}>
              <div style={s.field}>
                <KeyRound size={16} style={s.icon} />
                <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" name="reset-code" style={{...s.input, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700, fontFamily: "'Space Mono', monospace"}}
                  placeholder="000000" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} required />
              </div>
              <div style={s.field}><Lock size={16} style={s.icon} /><input style={s.input} type="password" autoComplete="new-password" name="new-password" placeholder={t('auth.newPassword')} value={newPassword} onChange={e => setNewPassword(e.target.value)} required /></div>
              <button style={s.btn} type="submit" disabled={loading}>{loading ? t('common.loading') : t('auth.resetPassword')} <ArrowRight size={16} /></button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#07080c', position: 'relative', overflow: 'hidden' },
  glow1: { position: 'absolute', top: '-30%', left: '-20%', width: '60%', height: '60%', background: 'radial-gradient(circle, rgba(139,198,62,0.07) 0%, transparent 70%)', pointerEvents: 'none' },
  glow2: { position: 'absolute', bottom: '-30%', right: '-20%', width: '60%', height: '60%', background: 'radial-gradient(circle, rgba(77,144,254,0.05) 0%, transparent 70%)', pointerEvents: 'none' },
  card: { background: '#0f1117', border: '1px solid #252733', borderRadius: 20, padding: '44px 36px', width: 400, maxWidth: '92vw', position: 'relative', zIndex: 1 },
  topRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 },
  brand: { display: 'flex', alignItems: 'center', gap: 10 },
  logo: { width: 40, height: 40, background: 'linear-gradient(135deg, #8BC63E, #00b4d8)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, color: '#07080c' },
  brandName: { fontWeight: 700, fontSize: 22, color: '#e4e5ed', letterSpacing: -0.5 },
  title: { fontSize: 24, fontWeight: 700, color: '#e4e5ed', marginBottom: 4 },
  sub: { fontSize: 14, color: '#8789a0', marginBottom: 24 },
  error: { background: 'rgba(255,68,102,0.1)', border: '1px solid rgba(255,68,102,0.2)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#ff4466', marginBottom: 16 },
  success: { background: 'rgba(139,198,62,0.1)', border: '1px solid rgba(139,198,62,0.2)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#8BC63E', marginBottom: 16 },
  field: { position: 'relative', marginBottom: 14 },
  icon: { position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#8a8da4', pointerEvents: 'none' },
  input: { width: '100%', background: '#161821', border: '1px solid #5c6078', borderRadius: 10, padding: '13px 14px 13px 42px', fontSize: 16, color: '#e4e5ed', outline: 'none', boxSizing: 'border-box' },
  forgotLink: { textAlign: 'right', fontSize: 12, color: '#8BC63E', cursor: 'pointer', marginBottom: 8, fontWeight: 500 },
  btn: { width: '100%', padding: '14px 0', background: '#8BC63E', color: '#07080c', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  switch: { textAlign: 'center', marginTop: 18, fontSize: 13, color: '#8789a0' },
  switchLink: { color: '#8BC63E', cursor: 'pointer', fontWeight: 600 },
  divider: { textAlign: 'center', margin: '20px 0', color: '#8a8da4', fontSize: 12 },
  pickupBtn: { width: '100%', padding: '13px 0', background: 'transparent', border: '1px solid #252733', borderRadius: 12, fontSize: 14, fontWeight: 600, color: '#8789a0', cursor: 'pointer' },
  backBtn: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#8789a0', cursor: 'pointer', marginBottom: 16 },
  resend: { textAlign: 'center', marginTop: 16, fontSize: 12, color: '#8BC63E', cursor: 'pointer' },
};
